import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/lib/auth/auth-context'
import { ATFLogo } from '@/components/assets/ATFLogo'

type LoginTab = 'password' | 'key'

// Genesis brand conformance pass (docs/decisions/DEPLOYMENT-LOG.md, "Genesis
// brand conformance — portal + console login"). Full Genesis palette
// (charcoal + gold) — operator decision: BOTH the customer portal and the
// operator console use the same charcoal/gold system, no teal and no
// steel-blue anywhere. Surface distinction between the two apps comes from
// the badge text (OPERATOR CONTROL PLANE here vs CUSTOMER PORTAL on the
// portal), copy, and hero content — not from a different accent color.
// Design tokens below match .claude/skills/atf-ee/references/genesis.md's
// Design Tokens table exactly. This pass touches the login page ONLY — the
// rest of the operator console shell still uses the prior steel-blue
// accent; see the pass's final summary for the follow-up list.
const GOLD = '#D4B85C'
const GOLD_DIM = 'rgba(212, 184, 92, 0.15)'
const CHARCOAL = '#18171A'
const CARD = '#21201F'
const WHITE = '#F5F4F0'
const GREY_300 = '#B0AFA8'
const GREY_400 = '#6E6D68'
const GREY_500 = '#4A4944'
const DANGER = '#F27B76'

const capabilities = [
  {
    sprite: '/mascot-error.svg',
    title: 'Issue & govern licenses',
    body: 'Provision, revoke, and audit customer entitlements from one place',
  },
  {
    sprite: '/mascot-idle.svg',
    title: 'Fleet-wide visibility',
    body: 'See every account, instance, and deployment across the platform',
  },
  {
    sprite: '/mascot-thinking.svg',
    title: 'Cross-account operations',
    body: 'Billing, support, and incident response without switching context',
  },
]

export function KeyEntryPage() {
  // Password is the primary path for a human operator with an account
  // (ADR-118/ADR-119 Phase 7); the API-key tab remains for operators who
  // authenticate with a console_ key (automation, or an operator who was
  // issued a key). Both land in the same authenticated app.
  const [tab, setTab] = useState<LoginTab>('password')

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: CHARCOAL }}>
      {/* Left side - operator branding panel (hidden on mobile). Gold grid
          pattern lives on this main background only (Genesis rule: never
          on cards). */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-1/2">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(212, 184, 92, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 184, 92, 0.02) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div
          className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(212, 184, 92, 0.3) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />

        <div
          className="absolute -bottom-48 -right-48 h-[400px] w-[400px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${GOLD_DIM} 0%, transparent 70%)`,
            filter: 'blur(80px)',
          }}
        />

        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <ATFLogo variant="full" size="lg" style={{ color: GOLD }} />
            <span
              className="ml-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
              style={{ backgroundColor: GOLD_DIM, color: GOLD }}
            >
              Operator Control Plane
            </span>
          </div>

          <div className="max-w-lg">
            <div className="mb-8 flex justify-center">
              <img src="/mascot-idle.svg" alt="" className="h-28 w-28 opacity-90" />
            </div>

            <h1
              className="font-display mb-4 font-semibold"
              style={{ color: WHITE, fontSize: '2.6rem', lineHeight: 1.1 }}
            >
              The ATF control plane
            </h1>
            <p className="mb-10 text-lg font-light" style={{ color: GREY_300 }}>
              Manage licenses, accounts, and instances across the fleet — one
              operator surface for everything running on the platform.
            </p>

            <div className="space-y-4">
              {capabilities.map(({ sprite, title, body }) => (
                <div
                  key={title}
                  className="flex items-center gap-5 rounded-xl p-5"
                  style={{ backgroundColor: CARD, border: '1px solid rgba(212, 184, 92, 0.12)' }}
                >
                  <img src={sprite} alt="" className="h-14 w-14 flex-shrink-0" />
                  <div>
                    <p className="font-sub text-base font-semibold" style={{ color: WHITE }}>
                      {title}
                    </p>
                    <p className="mt-1 text-sm" style={{ color: GREY_300 }}>
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm font-light" style={{ color: GREY_400 }}>
            Internal use only &middot; Operator access required
          </p>
        </div>
      </div>

      {/* Right side - sign-in card */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-8">
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(90deg, ${GOLD_DIM} 0%, transparent 50%)` }}
        />

        <div
          className="relative z-10 w-full max-w-sm rounded-xl p-6"
          style={{ backgroundColor: CARD, border: '1px solid rgba(212, 184, 92, 0.12)' }}
        >
          {/* Mobile logo */}
          <div className="mb-6 flex items-center justify-center gap-2 lg:hidden">
            <ATFLogo variant="full" size="md" style={{ color: GOLD }} />
          </div>

          <h2 className="font-sub mb-1 text-lg font-semibold" style={{ color: WHITE }}>
            Operator sign-in
          </h2>
          <p className="mb-5 text-sm" style={{ color: GREY_300 }}>
            Authenticate to access the control plane.
          </p>

          <GoogleSignInButton />

          <div className="mb-5 flex items-center gap-3">
            <div className="h-px flex-1" style={{ backgroundColor: GREY_500 }} />
            <span className="text-xs" style={{ color: GREY_400 }}>
              or
            </span>
            <div className="h-px flex-1" style={{ backgroundColor: GREY_500 }} />
          </div>

          <div
            role="tablist"
            aria-label="Sign-in method"
            className="mb-5 flex rounded-lg p-0.5 text-sm"
            style={{ border: `1px solid ${GREY_500}`, backgroundColor: CHARCOAL }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'password'}
              onClick={() => setTab('password')}
              className="flex-1 rounded-lg px-3 py-1.5 font-medium transition-colors"
              style={
                tab === 'password'
                  ? { backgroundColor: GOLD, color: CHARCOAL }
                  : { color: GREY_300 }
              }
            >
              Password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'key'}
              onClick={() => setTab('key')}
              className="flex-1 rounded-lg px-3 py-1.5 font-medium transition-colors"
              style={tab === 'key' ? { backgroundColor: GOLD, color: CHARCOAL } : { color: GREY_300 }}
            >
              API key
            </button>
          </div>

          {tab === 'password' ? <PasswordForm /> : <KeyForm />}
        </div>
      </div>
    </div>
  )
}

// Google Workspace sign-in (ADR-118/ADR-119 Phase 7 slice B, services/console/
// server/oidc.go). The backend flow is a plain GET redirect chain -- no
// client-side OAuth library needed, just a link to /auth/oidc/start, which
// the server answers 404 if CONSOLE_OIDC_CLIENT_ID/SECRET/ALLOWED_HD aren't
// all set (see that file's header). This button was the missing piece: the
// backend flow was built, configured, and live, but no UI ever linked to it.
function GoogleSignInButton() {
  return (
    <a
      href="/auth/oidc/start"
      className="mb-5 flex w-full items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors hover:brightness-110"
      style={{
        height: '42px',
        backgroundColor: WHITE,
        color: CHARCOAL,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9C16.64 14.2 17.64 11.9 17.64 9.2Z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.9v2.33A9 9 0 0 0 9 18Z"
        />
        <path
          fill="#FBBC05"
          d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.16.28-1.7V4.97H.9A9 9 0 0 0 0 9c0 1.45.35 2.83.9 4.03l3.05-2.33Z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .9 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58Z"
        />
      </svg>
      Sign in with Google
    </a>
  )
}

const inputStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  border: `1px solid ${GREY_500}`,
}

function focusRingClass() {
  return 'outline-none focus:ring-2'
}

function PasswordForm() {
  const { loginWithPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await loginWithPassword(email, password)
      // Never keep the password in component state after a submit.
      setPassword('')
      navigate({ to: '/' })
    } catch (err) {
      // loginWithPassword already collapses the 401 unknown-email/wrong-
      // password/disabled cases to a single generic message (no enumeration);
      // it surfaces a server message for other statuses (e.g. a disabled
      // account the server chose to name, or a backend error). Show whatever
      // it threw, falling back to the generic line.
      setError(err instanceof Error ? err.message : 'Invalid email or password.')
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <label htmlFor="operator-email" className="mb-1 block text-sm font-normal" style={{ color: GREY_300 }}>
        Email
      </label>
      <input
        id="operator-email"
        type="email"
        autoComplete="username"
        spellCheck={false}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className={`mb-1 w-full rounded-lg px-3 py-2.5 text-sm ${focusRingClass()}`}
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
        required
      />
      <p className="mb-3 text-xs" style={{ color: GREY_400 }}>
        The email address on your operator account
      </p>

      <label htmlFor="operator-password" className="mb-1 block text-sm font-normal" style={{ color: GREY_300 }}>
        Password
      </label>
      <input
        id="operator-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className={`mb-1 w-full rounded-lg px-3 py-2.5 text-sm ${focusRingClass()}`}
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
        required
      />
      <p className="mb-3 text-xs" style={{ color: GREY_400 }}>
        Your operator account password
      </p>

      {error && (
        <div className="mb-3 rounded-lg p-3" style={{ backgroundColor: CARD, borderLeft: `3px solid ${DANGER}` }}>
          <p className="text-xs" style={{ color: WHITE }}>
            {error}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || email.trim().length === 0 || password.length === 0}
        className="w-full rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ backgroundColor: GOLD, color: CHARCOAL, height: '42px' }}
      >
        {submitting ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  )
}

function KeyForm() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [key, setKey] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(key.trim(), remember)
      // Key is discarded from local state immediately on success; the
      // input is not re-rendered with the stored value anywhere in the
      // app (show-once semantics per ADR-119 D3 / task brief step 3 -- the
      // raw key is never redisplayed after acceptance).
      setKey('')
      navigate({ to: '/' })
    } catch {
      // login() surfaces any WhoAmI failure here -- an invalid, unknown,
      // revoked, expired, or disabled key all come back as the same
      // Unauthenticated status (ADR-119: no distinction is surfaced to the
      // client, fail-closed / no enumeration oracle), so this message is
      // deliberately generic rather than guessing which case applied.
      setError('That key was not accepted. Check it and try again.')
      setKey('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <p className="mb-4 text-sm" style={{ color: GREY_300 }}>
        Enter your operator API key to continue.
      </p>

      <label htmlFor="operator-api-key" className="mb-1 block text-sm font-normal" style={{ color: GREY_300 }}>
        Operator API key
      </label>
      <input
        id="operator-api-key"
        type="password"
        autoComplete="off"
        spellCheck={false}
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="console_xxxxxxxx_..."
        className={`mb-1 w-full rounded-lg px-3 py-2.5 font-mono text-sm ${focusRingClass()}`}
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
        required
      />
      <p className="mb-3 text-xs" style={{ color: GREY_400 }}>
        Issued to you by another operator; never displayed again after acceptance
      </p>

      <label className="mb-1 flex items-start gap-2 text-sm" style={{ color: GREY_300 }}>
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="mt-0.5"
        />
        <span>Remember on this device</span>
      </label>
      {remember && (
        <div className="mb-3 mt-1 rounded-lg p-2" style={{ backgroundColor: CARD, borderLeft: '3px solid #FBBF24' }}>
          <p className="text-xs" style={{ color: WHITE }}>
            Warning: this stores your API key in this browser's local storage
            in plaintext. Anything that can read local storage on this site
            (a browser extension, another user of this device, an XSS bug)
            can recover it. Only enable this on a device you trust and
            control.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg p-3" style={{ backgroundColor: CARD, borderLeft: `3px solid ${DANGER}` }}>
          <p className="text-xs" style={{ color: WHITE }}>
            {error}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || key.trim().length === 0}
        className="w-full rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ backgroundColor: GOLD, color: CHARCOAL, height: '42px' }}
      >
        {submitting ? 'Checking…' : 'Continue'}
      </button>
    </form>
  )
}
